import React from "react";
import { Button, Badge } from "react-bootstrap";
import moment from "moment";

const ProjectMilestoneTab = ({
  // Data
  milestones,
  milestonesLoading,
  milestoneTasks,
  expandedMilestones,
  taskAssignedUsers,

  // Permissions
  hasProjectAdminRole,
  hasProjectManagerRole,
  hasEditPermission,

  // Handlers
  handleCreateMilestone,
  handleEditMilestone,
  toggleMilestoneExpand,
  handleTaskNameClick,
  handleEditTask,
  formatDate,
  getStatusBadge,
  getPriorityBadge,
}) => {
  const canEdit = hasProjectAdminRole() || hasProjectManagerRole();

  return (
    <div className="project-view-fade-in">
      <div className="project-overview-single-card">
        <div
          className="d-flex justify-content-between align-items-center mb-4"
          style={{ margin: "15px" }}
        >
          <div>
            <h3 className="project-view-overview-section-title mb-1">
              Project Milestones
            </h3>
            <p className="text-muted mb-0">
              Track and manage project milestones and deliverables
            </p>
          </div>
          {canEdit && (
            <Button variant="primary" onClick={handleCreateMilestone}>
              Add Milestone
            </Button>
          )}
        </div>

        {milestonesLoading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading milestones...</span>
            </div>
          </div>
        ) : milestones.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="fas fa-flag fa-3x mb-3"></i>
            <h5>No Milestones Found</h5>
            <p className="mb-4">
              No milestones are defined for this project yet.
            </p>
            {canEdit && (
              <Button variant="outline-primary" onClick={handleCreateMilestone}>
                Add Your First Milestone
              </Button>
            )}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="project-view-milestone-table">
              <thead className="project-view-milestone-table-header">
                <tr>
                  <th style={{ width: "40px" }}></th>
                  <th>Milestone ID</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Percentage</th>
                  <th>Planned Dates</th>
                  <th>Actual Dates</th>
                  <th>Status</th>
                  <th>Description</th>
                  <th>Created By</th>
                  <th>Created Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="milestone-table-body">
                {milestones.map((milestone) => (
                  <React.Fragment key={milestone.milestoneMavroId}>
                    <tr className="milestone-table-row">
                      <td className="milestone-expand-cell">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="expand-btn"
                          onClick={() =>
                            toggleMilestoneExpand(milestone.milestoneMavroId)
                          }
                          title={
                            expandedMilestones[milestone.milestoneMavroId]
                              ? "Collapse tasks"
                              : "Expand tasks"
                          }
                        >
                          {expandedMilestones[milestone.milestoneMavroId]
                            ? "▲"
                            : "▼"}
                        </Button>
                      </td>
                      <td className="milestone-id-cell">
                        <div className="milestone-id">
                          {milestone.milestoneMavroId}
                        </div>
                      </td>
                      <td className="milestone-name-cell">
                        <div className="milestone-name">{milestone.name}</div>
                      </td>
                      <td className="milestone-type-cell">
                        <div className="milestone-type">
                          {milestone.type || "-"}
                        </div>
                      </td>
                      <td className="milestone-percentage-cell">
                        <div className="milestone-percentage">
                          {milestone.calculatedPercentage !== undefined ? (
                            <div>
                              <div className="percentage-value">
                                {milestone.calculatedPercentage}%
                              </div>
                              <div className="percentage-details small text-muted">
                                {milestone.completedTasks}/
                                {milestone.totalTasks} tasks
                              </div>
                            </div>
                          ) : milestone.percentage ? (
                            `${milestone.percentage}%`
                          ) : (
                            "-"
                          )}
                        </div>
                      </td>
                      <td className="milestone-planned-dates-cell">
                        <div className="milestone-dates">
                          <div>
                            <small className="text-muted">Start:</small>{" "}
                            {formatDate(milestone.plannedStartDate)}
                          </div>
                          <div>
                            <small className="text-muted">End:</small>{" "}
                            {formatDate(milestone.plannedEndDate)}
                          </div>
                        </div>
                      </td>
                      <td className="milestone-actual-dates-cell">
                        <div className="milestone-dates">
                          <div>
                            <small className="text-muted">Start:</small>{" "}
                            {formatDate(milestone.actualStartDate) || "-"}
                          </div>
                          <div>
                            <small className="text-muted">End:</small>{" "}
                            {formatDate(milestone.actualEndDate) || "-"}
                          </div>
                        </div>
                      </td>
                      <td className="milestone-status-cell">
                        <span
                          className={`milestone-status-badge ${getStatusBadge(milestone.status)}`}
                        >
                          {milestone.status}
                        </span>
                      </td>
                      <td className="milestone-description-cell">
                        <div className="milestone-description">
                          {milestone.description
                            ? milestone.description.length > 50
                              ? `${milestone.description.substring(0, 50)}...`
                              : milestone.description
                            : "-"}
                        </div>
                      </td>
                      <td className="milestone-createdby-cell">
                        <div className="milestone-createdby">
                          {milestone.createdBy || "-"}
                        </div>
                      </td>
                      <td className="milestone-date-cell">
                        {milestone.createdOnDate
                          ? formatDate(milestone.createdOnDate)
                          : "-"}
                      </td>
                      <td className="milestone-actions-cell">
                        <div className="milestone-actions">
                          {canEdit && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditMilestone(milestone)}
                              className="me-2"
                              title="Edit Milestone"
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded tasks row */}
                    {expandedMilestones[milestone.milestoneMavroId] && (
                      <tr className="milestone-tasks-row">
                        <td
                          colSpan="12"
                          className="project-view-milestone-tasks-container"
                        >
                          <div className="milestone-tasks-content">
                            <h6 className="tasks-section-title">
                              Tasks Assigned to this Milestone
                              <Badge bg="secondary" className="ms-2">
                                {milestoneTasks[milestone.milestoneMavroId]
                                  ?.length || 0}{" "}
                                tasks
                              </Badge>
                            </h6>

                            {milestoneTasks[milestone.milestoneMavroId]
                              ?.length > 0 ? (
                              <div className="table-responsive">
                                <table className="task-subtable">
                                  <thead className="task-subtable-header">
                                    <tr>
                                      <th>Task ID</th>
                                      <th>Task Name</th>
                                      <th>Description</th>
                                      <th>Assigned To</th>
                                      <th>Priority</th>
                                      <th>Status</th>
                                      <th>Start Date</th>
                                      <th>End Date</th>
                                      <th>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody className="task-subtable-body">
                                    {milestoneTasks[
                                      milestone.milestoneMavroId
                                    ].map((task) => (
                                      <tr
                                        key={task.taskId}
                                        className="task-subtable-row"
                                      >
                                        <td className="task-id-cell">
                                          {task.taskId}
                                        </td>
                                        <td className="project-view-task-name-cell">
                                          <div
                                            className="project-view-task-name clickable-task-name"
                                            onClick={() =>
                                              handleTaskNameClick(task.taskId)
                                            }
                                            title="Click to view task details"
                                          >
                                            {task.taskName}
                                          </div>
                                        </td>
                                        <td className="project-view-task-description-cell">
                                          <div className="project-view-task-description">
                                            {task.taskDescription || "-"}
                                          </div>
                                        </td>
                                        <td className="project-view-task-assigned-cell">
                                          <div className="task-assigned">
                                            {taskAssignedUsers[
                                              parseInt(task.assignedTo)
                                            ] ||
                                              task.assignedTo ||
                                              "Unassigned"}
                                          </div>
                                        </td>
                                        <td className="project-view-task-priority-cell">
                                          <span
                                            className={`project-view-task-priority-badge ${getPriorityBadge(task.taskPriority)}`}
                                          >
                                            {task.taskPriority}
                                          </span>
                                        </td>
                                        <td className="project-view-task-status-cell">
                                          <span
                                            className={`project-view-task-status-badge ${getStatusBadge(task.taskStatus)}`}
                                          >
                                            {task.taskStatus}
                                          </span>
                                        </td>
                                        <td className="project-view-task-date-cell">
                                          {moment(task.taskStartDate).format(
                                            "MMM DD, YYYY",
                                          )}
                                        </td>
                                        <td className="project-view-task-date-cell">
                                          {moment(task.taskEndDate).format(
                                            "MMM DD, YYYY",
                                          )}
                                        </td>
                                        <td className="project-view-task-actions-cell">
                                          <div className="project-view-task-actions">
                                            {hasEditPermission() && (
                                              <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() =>
                                                  handleEditTask(task.taskId)
                                                }
                                                className="me-2"
                                                title="Edit Task"
                                              >
                                                Edit
                                              </Button>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="text-center py-3 text-muted">
                                <i className="fas fa-tasks fa-2x mb-2"></i>
                                <p>No tasks assigned to this milestone</p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectMilestoneTab;
